/**
 * Phase 19 — "Today's Surprises" section on the customer Cards page.
 *
 * Per spec (PHASE_19, §UI):
 *   • NO animations, NO slot-machine framing.
 *   • One card per enrolled tenant that has an enabled pool.
 *   • States: AVAILABLE (Claim + Skip), CLAIMED (outcome), SKIPPED (greyed).
 *   • NO_PRIZE labelled honestly ("No prize today").
 *   • Multi-tenant: stack vertically, no carousel.
 *
 * Renders nothing while loading (the rest of Cards is more important
 * than a spinner here) or when the customer has zero AVAILABLE/CLAIMED/
 * SKIPPED surprises today.
 */
import {
  useClaimSurprise,
  useSkipSurprise,
  useTodaysSurprises,
} from "@orbit/api-client-react";
import type { SurpriseCardWire } from "@orbit/api-zod";

export function TodaysSurprisesSection() {
  const q = useTodaysSurprises();
  const cards = q.data?.cards ?? [];
  if (q.isLoading) return null;
  if (q.isError) return null;
  if (cards.length === 0) return null;

  return (
    <section
      className="mt-5"
      aria-labelledby="todays-surprises-heading"
      data-testid="todays-surprises"
    >
      <h2
        id="todays-surprises-heading"
        className="text-xs font-semibold uppercase tracking-wider text-ink-dark"
      >
        Today's Surprises
      </h2>
      <ul className="mt-3 flex flex-col gap-3">
        {cards.map((card) => (
          <li key={card.tenantId}>
            <SurpriseRow card={card} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function SurpriseRow({ card }: { card: SurpriseCardWire }) {
  const claim = useClaimSurprise();
  const skip = useSkipSurprise();
  const busy = claim.isPending || skip.isPending;

  // Tenant brand-color tint when available; falls back to navy. Keep
  // the surface restrained — this is a daily-habit nudge, not a prize
  // wheel.
  const tint = card.tenantBrandColorPrimary ?? "#0B2447";

  if (card.state === "EXPIRED") {
    return (
      <article
        className="rounded-2xl border border-ink-light/40 bg-white px-4 py-3 opacity-60"
        data-testid={`surprise-card-${card.tenantId}`}
        data-state="EXPIRED"
      >
        <p className="text-sm font-semibold text-navy">{card.tenantName}</p>
        <p className="mt-1 text-xs text-ink-dark">
          Yesterday's surprise expired. Check back tomorrow.
        </p>
      </article>
    );
  }

  if (card.state === "SKIPPED") {
    return (
      <article
        className="rounded-2xl border border-ink-light/40 bg-white px-4 py-3 opacity-60"
        data-testid={`surprise-card-${card.tenantId}`}
        data-state="SKIPPED"
      >
        <p className="text-sm font-semibold text-navy">{card.tenantName}</p>
        <p className="mt-1 text-xs text-ink-dark">
          Skipped for today. New surprise tomorrow.
        </p>
      </article>
    );
  }

  if (card.state === "CLAIMED") {
    return (
      <article
        className="rounded-2xl border border-ink-light/40 bg-white px-4 py-3"
        data-testid={`surprise-card-${card.tenantId}`}
        data-state="CLAIMED"
        style={{ borderLeftWidth: 4, borderLeftColor: tint }}
      >
        <p className="text-sm font-semibold text-navy">{card.tenantName}</p>
        <p className="mt-1 text-xs text-ink-dark">{card.displayLabel}</p>
        <ClaimedOutcomeLine card={card} />
      </article>
    );
  }

  // AVAILABLE
  return (
    <article
      className="rounded-2xl border border-ink-light/40 bg-white px-4 py-3"
      data-testid={`surprise-card-${card.tenantId}`}
      data-state="AVAILABLE"
      style={{ borderLeftWidth: 4, borderLeftColor: tint }}
    >
      <p className="text-sm font-semibold text-navy">{card.tenantName}</p>
      <p className="mt-1 text-xs text-ink-dark">
        Today's surprise is waiting. Tap Reveal to see what's inside.
      </p>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          className="rounded-lg bg-navy px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
          onClick={() => claim.mutate({ tenantId: card.tenantId })}
          disabled={busy}
          data-testid={`surprise-claim-${card.tenantId}`}
        >
          {claim.isPending ? "Revealing…" : "Reveal"}
        </button>
        <button
          type="button"
          className="rounded-lg px-3 py-1.5 text-xs font-medium text-navy hover:bg-navy/5 disabled:opacity-60"
          onClick={() => skip.mutate({ tenantId: card.tenantId })}
          disabled={busy}
          data-testid={`surprise-skip-${card.tenantId}`}
        >
          Skip
        </button>
      </div>
      {(claim.isError || skip.isError) && (
        <p className="mt-2 text-xs text-red-600">
          {claim.error?.message ?? skip.error?.message ?? "Couldn't update."}
        </p>
      )}
    </article>
  );
}

function ClaimedOutcomeLine({ card }: { card: SurpriseCardWire }) {
  const o = card.outcome;
  if (!o) return null;
  if (o.kind === "POINTS_CREDITED") {
    return (
      <p className="mt-1 text-xs font-medium text-emerald-700">
        +{(o.pointsCredited ?? 0).toFixed(2)} points credited.
      </p>
    );
  }
  if (o.kind === "STAMPS_CREDITED") {
    return (
      <p className="mt-1 text-xs font-medium text-emerald-700">
        +{o.stampsCredited ?? 0} stamps credited.
      </p>
    );
  }
  if (o.kind === "VOUCHER_ISSUED") {
    return (
      <p className="mt-1 text-xs font-medium text-emerald-700">
        Voucher issued{o.voucherCode ? `: ${o.voucherCode}` : "."}
        {o.rewardName ? ` — ${o.rewardName}` : ""}
      </p>
    );
  }
  // NO_PRIZE — labelled honestly per Decision D71.
  return (
    <p className="mt-1 text-xs font-medium text-ink-dark">
      No prize today. Try again tomorrow.
    </p>
  );
}
